import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import Typography from '@mui/material/Typography';
import { Grid } from '@mui/system';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete';
import { RHFTextField } from '@openx/components-core/lib/TextField/RHFTextField';
import {
  type DealFormDataWithTargeting,
  dealFormField,
  type RboConfig,
  type RboConfigField,
  rboConfigField,
} from '@openx/types/deal';
import { noop } from '@openx/utils/noop';
import findKey from 'lodash/findKey';
import { forwardRef } from 'react';
import type { UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NumericFormat, type NumericFormatProps } from 'react-number-format';
import type { useRboFields } from '../../../hooks/useRboFields';

const autocompleteFields: RboConfigField[] = [
  rboConfigField.TACTIC,
  rboConfigField.TARGET,
  rboConfigField.MODEL_GOAL,
  rboConfigField.CHANNEL,
  rboConfigField.TACTIC,
];

const textFields: RboConfigField[] = [rboConfigField.CUSTOM_SCORE_NAME];

const numericFields: RboConfigField[] = [
  rboConfigField.DROP_PERCENT,
  rboConfigField.HOURLY_TARGET,
  rboConfigField.THRESHOLD,
];

const nonAutocompleteFields = [...textFields, ...numericFields];

type CustomProps = {
  onChange: (event: {
    target: { name: string; value: string };
    preventDefault(): void;
    isDefaultPrevented(): boolean;
  }) => void;
  name: string;
};

type NumericFormatCustomProps = CustomProps & NumericFormatProps & InputBaseComponentProps;

const NumericFormatCustom = forwardRef<HTMLInputElement, NumericFormatCustomProps>((props, ref) => {
  const { onChange, ...other } = props;

  return (
    <NumericFormat
      {...other}
      getInputRef={ref}
      onValueChange={({ value }, { event }) =>
        onChange({
          preventDefault: event?.preventDefault || noop,
          isDefaultPrevented: event?.isDefaultPrevented || (() => true),
          target: { ...event?.target, value, name: props.name },
        })
      }
      thousandSeparator
      valueIsNumericString
    />
  );
});

type RboFieldsReturn = ReturnType<typeof useRboFields>;

type Props = {
  rboFields: RboFieldsReturn;
  modelGoal: string | undefined;
  rboAdmin: boolean;
  rboLabels: Record<string, string>;
  rboPlaceholders: Record<string, string>;
  setValue: UseFormSetValue<DealFormDataWithTargeting>;
  rboErrors: Record<string, unknown>;
  observableRboConfig: RboConfig | undefined;
  debouncedValidation: (field: RboConfigField) => void;
  shouldDisableGoalField: boolean;
  handleClearField: (field: RboConfigField) => void;
};

export const CustomSectionFields = ({
  rboFields,
  modelGoal,
  rboAdmin,
  rboLabels,
  rboPlaceholders,
  setValue,
  rboErrors,
  observableRboConfig,
  debouncedValidation,
  shouldDisableGoalField,
  handleClearField,
}: Props) => {
  const { t } = useTranslation();

  const handleAutocompleteInputChange = (field: RboConfigField, options: Record<string, string>) => {
    return (_: unknown, value: string) => {
      const optionValue = findKey(options, optionLabel => optionLabel === value) ?? value;
      setValue(`${dealFormField.RBO_CONFIG}.${field}`, value === '' ? null : optionValue, {
        shouldDirty: true,
      });
      debouncedValidation(field);
    };
  };

  const handleTextFieldChange = (field: RboConfigField) => (value: string) => {
    if (value === '') {
      setValue(`${dealFormField.RBO_CONFIG}.${field}`, null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    debouncedValidation(field);
  };

  return (
    <Grid
      container
      data-test="custom-section-fields"
      sx={{
        mt: 3,
        width: '100%',
      }}
    >
      <Grid
        sx={{
          width: '100%',
        }}
      >
        <Typography
          variant="h3"
          sx={{
            mb: 1,
          }}
        >
          {t('Custom Settings')}
        </Typography>
        {rboFields.map(({ field, options = {} }) => {
          const dataTestField = field.replaceAll('_', '-');

          // Hide Channel field if Goal is Conversion
          if (field === rboConfigField.CHANNEL && modelGoal === 'conversion') {
            return null;
          }

          if (autocompleteFields.includes(field)) {
            const isGoalField = field === rboConfigField.MODEL_GOAL;

            return (
              <Box
                key={field}
                sx={{
                  mb: 1.5,
                }}
              >
                <RHFAutoComplete
                  name={`${dealFormField.RBO_CONFIG}.${field}`}
                  textFieldProps={{
                    label: rboLabels[field],
                    placeholder: rboPlaceholders[field],
                  }}
                  options={Object.keys(options).toSorted()}
                  getOptionLabel={option => options[option] ?? option}
                  renderOptions={{
                    dataTest: `${dataTestField}-option`,
                  }}
                  onInputChange={rboAdmin ? handleAutocompleteInputChange(field, options) : undefined}
                  data-test={`rbo-${dataTestField}-input`}
                  fullWidth
                  freeSolo={rboAdmin}
                  disabled={isGoalField && shouldDisableGoalField}
                />
              </Box>
            );
          }

          if (nonAutocompleteFields.includes(field)) {
            return (
              <FormControl
                key={field}
                data-test={`rbo-${dataTestField}-input`}
                margin="dense"
                sx={{ mb: 1.5, mt: 0 }}
                fullWidth
              >
                <FormLabel error={!!rboErrors[field]}>{rboLabels[field]}</FormLabel>
                <RHFTextField
                  name={`${dealFormField.RBO_CONFIG}.${field}`}
                  placeholder={rboPlaceholders[field]}
                  slotProps={{
                    input: {
                      endAdornment: observableRboConfig?.[field] ? (
                        <InputAdornment position="end">
                          <IconButton onClick={() => handleClearField(field)} size="small">
                            <Close />
                          </IconButton>
                        </InputAdornment>
                      ) : undefined,
                      inputComponent: numericFields.includes(field) ? NumericFormatCustom : undefined,
                    },
                  }}
                  onChangeComplete={handleTextFieldChange(field)}
                  variant="outlined"
                  fullWidth
                />
              </FormControl>
            );
          }

          return null;
        })}
      </Grid>
    </Grid>
  );
};
